import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { FaArrowLeft, FaEnvelope, FaPaperPlane, FaUniversity } from "react-icons/fa";
import axiosInstance from "../api/axiosInstance";
import styles from "./PaymentRequisites.module.css";

const formatDate = (value) => {
  const date = value ? new Date(value) : new Date();
  if (Number.isNaN(date.getTime())) return new Date().toLocaleDateString("uk-UA");
  return date.toLocaleDateString("uk-UA");
};

const formatAmount = (value) => {
  const normalizedValue = Number(value);
  if (Number.isNaN(normalizedValue)) return "—";

  return new Intl.NumberFormat("uk-UA", {
    style: "currency",
    currency: "UAH",
    maximumFractionDigits: 0,
  }).format(normalizedValue);
};

const STORAGE_KEY = "lastPaymentRequisitesOrderNumber";

const hasPaymentDetails = (details = {}) =>
  Boolean(details.recipientName || details.iban || details.edrpou || details.bankName);

const getInitialOrderSnapshot = (state) => ({
  orderNumber: state?.orderNumber || sessionStorage.getItem(STORAGE_KEY) || "",
  createdAt: state?.createdAt,
  customerEmail: state?.customerEmail || "",
  totalAmount: state?.totalAmount,
  paymentDetails: state?.paymentDetails || null,
});

const PaymentRequisites = () => {
  const navigate = useNavigate();
  const { state } = useLocation();

  const [orderSnapshot, setOrderSnapshot] = useState(() =>
    getInitialOrderSnapshot(state)
  );
  const [isLoadingPaymentDetails, setIsLoadingPaymentDetails] = useState(false);
  const hasVisiblePaymentDetails = hasPaymentDetails(orderSnapshot.paymentDetails);

  useEffect(() => {
    setOrderSnapshot((prev) => ({
      ...prev,
      ...getInitialOrderSnapshot(state),
    }));
  }, [state]);

  useEffect(() => {
    if (orderSnapshot.orderNumber) {
      sessionStorage.setItem(STORAGE_KEY, String(orderSnapshot.orderNumber));
    }
  }, [orderSnapshot.orderNumber]);

  useEffect(() => {
    if (!orderSnapshot.orderNumber || hasVisiblePaymentDetails) return;

    let isCancelled = false;
    setIsLoadingPaymentDetails(true);

    axiosInstance
      .get(`/orders/${orderSnapshot.orderNumber}/payment-details`)
      .then((response) => {
        if (isCancelled) return;

        setOrderSnapshot((prev) => ({
          ...prev,
          orderNumber: response.data?.orderNumber || prev.orderNumber,
          createdAt: response.data?.createdAt || prev.createdAt,
          totalAmount: response.data?.totalAmount ?? prev.totalAmount,
          paymentDetails: response.data?.paymentDetails || prev.paymentDetails,
        }));
      })
      .catch((error) => {
        console.error("❌ Не вдалося завантажити реквізити:", error);
      })
      .finally(() => {
        if (!isCancelled) setIsLoadingPaymentDetails(false);
      });

    return () => {
      isCancelled = true;
    };
  }, [orderSnapshot.orderNumber, hasVisiblePaymentDetails]);

  const orderNumber = orderSnapshot.orderNumber;
  const createdAt = formatDate(orderSnapshot.createdAt);
  const customerEmailFromOrder = orderSnapshot.customerEmail || "";
  const paymentDetails = orderSnapshot.paymentDetails || {};
  const paymentPurpose =
    paymentDetails.paymentPurpose ||
    `${paymentDetails.purposePrefix || "Оплата замовлення"} №${orderNumber || "—"}`;
  const orderTotalAmount = formatAmount(paymentDetails.amount ?? orderSnapshot.totalAmount);
  const displayValue = (value) => value || (isLoadingPaymentDetails ? "Завантажуємо..." : "—");

  const [email, setEmail] = useState(customerEmailFromOrder);
  const [isSending, setIsSending] = useState(false);
  const [sendMessage, setSendMessage] = useState("");

  useEffect(() => {
    if (customerEmailFromOrder && !email) {
      setEmail(customerEmailFromOrder);
    }
  }, [customerEmailFromOrder, email]);

  const handleSendToEmail = async () => {
    if (!orderNumber) {
      setSendMessage("Не вдалося визначити номер замовлення.");
      return;
    }

    if (!email.trim()) {
      setSendMessage("Вкажіть email, щоб надіслати реквізити.");
      return;
    }

    setIsSending(true);
    setSendMessage("");

    try {
      const response = await axiosInstance.post(`/orders/${orderNumber}/email/payment-details`, {
        email: email.trim(),
      });
      setSendMessage(
        response.data?.success
          ? "Реквізити успішно надіслані на вашу електронну пошту."
          : "Запит прийнято, але лист не був надісланий. Зверніться до менеджера."
      );
    } catch (error) {
      console.error("❌ Не вдалося надіслати реквізити:", error);
      setSendMessage(
        "Не вдалося надіслати реквізити автоматично. Перевірте email або зверніться до менеджера."
      );
    } finally {
      setIsSending(false);
    }
  };

  return (
    <div className={styles.paymentRequisitesPage}>
      <div className={styles.paymentRequisitesCard}>
        <h1 className={styles.paymentRequisitesTitle}>
          <FaUniversity /> Реквізити для оплати
        </h1>

        <p className={styles.paymentRequisitesOrderInfo}>
          Вітаємо! Замовлення №<strong>{orderNumber || "—"}</strong> успішно створено.
        </p>

        <div className={styles.paymentRequisitesBlock}>
          <h2>Безготівкова оплата</h2>
          <div className={styles.paymentRequisitesRows}>
            <div className={styles.paymentRequisitesRow}>
              <span>Дата замовлення:</span>
              <strong>{createdAt}</strong>
            </div>
            <div className={styles.paymentRequisitesRow}>
              <span>Сума до оплати:</span>
              <strong>{orderTotalAmount}</strong>
            </div>
            <div className={styles.paymentRequisitesRow}>
              <span>Отримувач:</span>
              <strong>{displayValue(paymentDetails.recipientName)}</strong>
            </div>
            <div className={styles.paymentRequisitesRow}>
              <span>IBAN:</span>
              <strong>{displayValue(paymentDetails.iban)}</strong>
            </div>
            <div className={styles.paymentRequisitesRow}>
              <span>ЄДРПОУ / РНОКПП:</span>
              <strong>{displayValue(paymentDetails.edrpou)}</strong>
            </div>
            <div className={styles.paymentRequisitesRow}>
              <span>Банк:</span>
              <strong>{displayValue(paymentDetails.bankName)}</strong>
            </div>
            <div className={styles.paymentRequisitesRow}>
              <span>Призначення платежу:</span>
              <strong>{paymentPurpose}</strong>
            </div>
          </div>
        </div>

        <div className={styles.paymentRequisitesNotice}>
          Відправлення замовлення здійснюється після надходження <strong>100% оплати</strong> на рахунок
          продавця!
        </div>

        <div className={styles.paymentRequisitesEmailSentInfo}>
          <FaEnvelope />
          <span>Ці реквізити вже показані на сторінці. За потреби продублюйте їх на електронну пошту.</span>
        </div>

        <div className={styles.paymentRequisitesEmailForm}>
          <label htmlFor="payment-email">Надіслати реквізити для оплати на електронну пошту</label>
          <input
            id="payment-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="example@email.com"
          />
          <button
            type="button"
            onClick={handleSendToEmail}
            className={styles.paymentRequisitesSendButton}
            disabled={isSending}
          >
            <FaPaperPlane /> {isSending ? "Надсилаємо..." : "Надіслати реквізити"}
          </button>
          {sendMessage && (
            <p className={styles.paymentRequisitesSendMessage}>{sendMessage}</p>
          )}
          </div>

        <button className={styles.paymentRequisitesBackButton} onClick={() => navigate("/")}>
          <FaArrowLeft /> Повернутися на головну сторінку
        </button>
      </div>
    </div>
  );
};

export default PaymentRequisites;
